import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Search, Eye, Calendar, FileText, Edit, Trash2, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { TestResults } from "./TestResults";
import { useUserRoles } from "@/hooks/useUserRoles";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { MultiSelect } from "@/components/ui/multi-select";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { atticaMunicipalities } from "./atticaMunicipalities";
import { municipalityRegionMap } from "./municipalityRegions";
import { useLanguage } from "@/contexts/LanguageContext";

interface Candidate {
  id: string;
  full_name: string;
  email: string;
  phone: string;
  status: string;
  application_date: string;
  notes?: string;
  suburbs?: string[];
}

interface CandidateListProps {
  onCandidateChange?: () => void;
  statusFilter?: 'under_review' | 'hired' | 'rejected' | 'archived';
}

export function CandidateList({ onCandidateChange, statusFilter = 'under_review' }: CandidateListProps) {
  const { t } = useLanguage();
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSuburbs, setSelectedSuburbs] = useState<string[]>([]);
  const [region, setRegion] = useState<'all' | 'central' | 'north' | 'south' | 'west' | 'east' | 'piraeus'>("all");
  const [loading, setLoading] = useState(true);
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null);
  const [showTestResults, setShowTestResults] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { isSuperAdmin, isAdmin, isUnassignedAdmin, adminTeamIds } = useUserRoles();

  const regionOptions = [
    { value: 'all', label: t('allRegions') },
    { value: 'central', label: t('centralSector') },
    { value: 'north', label: t('north') },
    { value: 'south', label: t('south') },
    { value: 'west', label: t('west') },
    { value: 'east', label: t('east') },
    { value: 'piraeus', label: t('piraeus') },
  ] as const;

  useEffect(() => {
    fetchCandidates();
  }, []);

  const fetchCandidates = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // RLS policies handle access control automatically
      // Super admins see all candidates, regular admins see their admin group's candidates
      const { data, error } = await supabase
        .from('candidates')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCandidates(data || []);
      onCandidateChange?.();
    } catch (error: any) {
      toast({
        title: "Σφάλμα",
        description: "Αποτυχία φόρτωσης υποψηφίων",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteCandidate = async (candidateId: string) => {
    try {
      console.log('Starting deletion process for candidate:', candidateId);
      
      // Use the database function to safely delete candidate with all dependencies
      const { data, error } = await supabase.rpc('delete_candidate_with_dependencies', {
        candidate_id_param: candidateId
      });

      if (error) {
        console.error('Database function error:', error);
        throw error;
      }

      // Type guard for the response data
      const result = data as any;
      
      if (result && !result.success) {
        console.error('Deletion failed:', result.error);
        throw new Error(result.error || 'Unknown deletion error');
      }

      console.log('Candidate deletion completed successfully:', result);
      
      // Create description with deletion counts if available
      let description = "Ο υποψήφιος και όλα τα συνδεδεμένα δεδομένα διαγράφηκαν επιτυχώς";
      
      if (result && result.deleted_counts) {
        const counts = Object.entries(result.deleted_counts as Record<string, number>)
          .filter(([, count]) => count > 0)
          .map(([table, count]) => `${table}: ${count}`)
          .join(', ');
        if (counts) {
          description += `. Διαγράφηκαν: ${counts}`;
        }
      }

      toast({
        title: "Επιτυχία",
        description
      });
      
      fetchCandidates();
    } catch (error: any) {
      console.error('Delete candidate error:', error);
      toast({
        title: "Σφάλμα",
        description: `Αποτυχία διαγραφής υποψηφίου: ${error.message}`,
        variant: "destructive",
      });
    }
  };

  const reconsiderCandidate = async (candidate: Candidate) => {
    try {
      // Reopen testing flow
      await supabase
        .from('candidates')
        .update({ status: 'first_test', rejection_reason: null })
        .eq('id', candidate.id);

      // Prepare workflow stages: make tests available again, reset final decision
      await supabase.from('hr_workflow_stages').upsert([
        { candidate_id: candidate.id, stage_name: 'occupational_testing', status: 'available', notes: 'Επανεξέταση: διαθέσιμα τεστ' },
        { candidate_id: candidate.id, stage_name: 'first_test', status: 'pending', notes: 'Επανεξέταση: πρώτη δοκιμή διαθέσιμη' },
        { candidate_id: candidate.id, stage_name: 'final_decision', status: 'pending', notes: 'Επανεξέταση: επανέναρξη διαδικασίας' },
      ]);

      toast({ title: 'Επανεξέταση', description: 'Ο υποψήφιος επανεντάχθηκε στη διαδικασία τεστ.' });
      fetchCandidates();
      // Προώθηση στη σελίδα υποψηφίου για άμεση συνέχεια
      navigate(`/hr/candidate/${candidate.id}`);
    } catch (error: any) {
      toast({ title: 'Σφάλμα', description: error.message, variant: 'destructive' });
    }
  };
  const filteredCandidates = candidates.filter(candidate => {
    // Text search filter
    const matchesSearchTerm = candidate.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (candidate.email && candidate.email.toLowerCase().includes(searchTerm.toLowerCase()));

    // Suburb filter - if no suburbs selected, show all candidates
    // If suburbs are selected, show candidates that have at least one matching suburb
    const matchesSuburbs = selectedSuburbs.length === 0 || 
      (candidate.suburbs && candidate.suburbs.some(suburb => selectedSuburbs.includes(suburb)));

    // Region filter - if region is selected, match any suburb that maps to it
    const matchesRegion = region === 'all' || (
      candidate.suburbs && candidate.suburbs.some(suburb => municipalityRegionMap[suburb] === region)
    );

    // Status filter
    const matchesStatus = statusFilter === 'hired'
      ? candidate.status === 'hired'
      : statusFilter === 'rejected'
        ? candidate.status === 'rejected'
        : statusFilter === 'archived'
          ? candidate.status === 'archived'
          : (candidate.status !== 'hired' && candidate.status !== 'rejected' && candidate.status !== 'archived');

    return matchesSearchTerm && matchesSuburbs && matchesRegion && matchesStatus;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'applied': return 'bg-blue-100 text-blue-800';
      case 'first_interview_scheduled': return 'bg-yellow-100 text-yellow-800';
      case 'first_interview_completed': return 'bg-orange-100 text-orange-800';
      case 'second_interview_scheduled': return 'bg-purple-100 text-purple-800';
      case 'tests_available': return 'bg-indigo-100 text-indigo-800';
      case 'ready_to_hire': return 'bg-green-100 text-green-800';
      case 'hired': return 'bg-emerald-100 text-emerald-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatStatus = (status: string) => {
    const statusMap: Record<string, string> = {
      'applied': 'Αίτηση Υποβλήθηκε',
      'first_interview_scheduled': '1η Συνέντευξη Προγραμματισμένη',
      'first_interview_completed': '1η Συνέντευξη Ολοκληρώθηκε',
      'second_interview_scheduled': '2η Συνέντευξη Προγραμματισμένη',
      'tests_available': 'Διαθέσιμα Τεστ',
      'ready_to_hire': 'Έτοιμος για Πρόσληψη',
      'hired': 'Προσλήφθηκε',
      'rejected': 'Απορρίφθηκε'
    };
    return statusMap[status] || status;
  };

  if (loading) {
    return <div className="text-center py-8">Φόρτωση υποψηφίων...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex items-center space-x-2 flex-1">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('searchCandidates')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full"
          />
        </div>
        
        <div className="flex gap-2 flex-1 sm:max-w-xl">
          <div className="w-44">
            <Select value={region} onValueChange={(v) => setRegion(v as any)}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t('region')} />
              </SelectTrigger>
              <SelectContent className="z-50 bg-background">
                {regionOptions.map((r) => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1">
            <MultiSelect
              options={atticaMunicipalities}
              selected={selectedSuburbs}
              onChange={setSelectedSuburbs}
              placeholder={t('filterByMunicipality')}
            />
          </div>
        </div>
      </div>

      <div className="grid gap-4">
        {filteredCandidates.map((candidate) => (
          <Card key={candidate.id}>
            <CardHeader>
              <div className="flex flex-col sm:flex-row justify-between items-start gap-3">
                <div className="flex-1">
                  <CardTitle className="text-base sm:text-lg">{candidate.full_name}</CardTitle>
                  <p className="text-sm text-muted-foreground break-all">{candidate.email}</p>
                  <p className="text-sm text-muted-foreground">{candidate.phone}</p>
                </div>
                <Badge className={`${getStatusColor(candidate.status)} text-xs sm:text-sm shrink-0`}>
                  {formatStatus(candidate.status)}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground">
                    Αίτηση: {new Date(candidate.application_date).toLocaleDateString('el-GR')}
                  </p>
                  {candidate.notes && (
                    <p className="text-sm mt-1 break-words">{candidate.notes}</p>
                  )}
                </div>
                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                  <Button
                    size="sm"
                    onClick={() => navigate(`/hr/candidate/${candidate.id}`)}
                    className="w-full sm:w-auto"
                  >
                    <ArrowRight className="h-4 w-4 mr-1" />
                    <span className="hidden sm:inline">Διαχείριση</span>
                    <span className="sm:hidden">Διαχείριση</span>
                  </Button>
                  
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setSelectedCandidate(candidate);
                      setShowTestResults(true);
                    }}
                    className="w-full sm:w-auto"
                  >
                    <FileText className="h-4 w-4 mr-1" />
                    <span className="hidden sm:inline">Αποτελέσματα</span>
                    <span className="sm:hidden">Τεστ</span>
                  </Button>
                  
                  {candidate.status === 'rejected' && (
                    <Button
                      size="sm"
                      className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700"
                      onClick={() => reconsiderCandidate(candidate)}
                    >
                      Επανεξέταση
                    </Button>
                  )}
                  
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="outline" className="w-full sm:w-auto">
                        <Trash2 className="h-4 w-4 text-destructive sm:mr-0" />
                        <span className="ml-1 sm:hidden">Διαγραφή</span>
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="mx-4 max-w-lg">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Διαγραφή Υποψηφίου</AlertDialogTitle>
                        <AlertDialogDescription>
                          Είστε βέβαιοι ότι θέλετε να διαγράψετε τον {candidate.full_name}; Αυτή η ενέργεια δεν μπορεί να αναιρεθεί.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Ακύρωση</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={() => deleteCandidate(candidate.id)}
                          className="bg-destructive text-destructive-foreground"
                        >
                          Διαγραφή
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredCandidates.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          Δεν βρέθηκαν υποψήφιοι
        </div>
      )}

      {/* Test Results Dialog */}
      <Dialog open={showTestResults} onOpenChange={setShowTestResults}>
        <DialogContent className="mx-4 w-[calc(100vw-2rem)] max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">
              Αποτελέσματα Τεστ - {selectedCandidate?.full_name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedCandidate && (
            <TestResults
              candidateId={selectedCandidate.id}
              candidateName={selectedCandidate.full_name}
              onClose={() => setShowTestResults(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}